// ── useManifestDragDrop — DRY drag-and-drop ingest for JSON manifests ───────
// Provides isDragOver state (for blur overlay) and event handlers that can be
// spread onto any container element.  Runs dropped files through the shared
// ingest pipeline (Single-Responsibility) and calls back with the result.
import { useCallback, useRef, useState } from 'react';
import { ingestManifestFile, type IngestResult } from '../utils';

interface UseDragDropOpts {
  /** Called after a dropped file passes (or fails) the ingest pipeline. */
  onIngest: (result: IngestResult) => void;
  /** When true the drop-zone is inert. */
  disabled?: boolean;
}

/**
 * Encapsulates the drag-enter/leave counting trick to avoid flicker when
 * the cursor moves between child elements, and feeds dropped files through
 * the manifest ingest pipeline.
 */
export function useManifestDragDrop({ onIngest, disabled }: UseDragDropOpts) {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounter = useRef(0);

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      dragCounter.current++;
      if (dragCounter.current === 1) setIsDragOver(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragOver(false);
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragOver(false);
      if (disabled) return;

      const files = e.dataTransfer.files;
      if (!files.length) return;

      // Process the first valid file only (multi-file would be confusing UX)
      const result = await ingestManifestFile(files[0]);
      onIngest(result);
    },
    [disabled, onIngest],
  );

  return {
    /** true while a drag payload hovers over the drop-zone */
    isDragOver,
    /** Spread these onto the container element */
    dragHandlers: { onDragEnter, onDragLeave, onDragOver, onDrop },
  } as const;
}
